import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TableSortLabel from "@mui/material/TableSortLabel";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
} */

function normalizeNumericValue(value) {
  if (value === null || value === undefined || value === '') {
      return null;
  }

  if (typeof value === 'number') {
      return value;
  }

  // Funciona con valores como "1.250", "1.250,50" o "1250.50"
  const stringValue = String(value).trim();

  let normalizedValue;

  if (stringValue.includes(',')) {
      normalizedValue = stringValue
          .replace(/\./g, '')
          .replace(',', '.');
  } else {
      normalizedValue = stringValue;
  }

  const number = Number(normalizedValue);

  return Number.isNaN(number) ? null : number;
}

function descendingComparator(a, b, orderBy, numeric = false) {
  let valueA = a[orderBy];
  let valueB = b[orderBy];

  if (numeric) {
      valueA = normalizeNumericValue(valueA);
      valueB = normalizeNumericValue(valueB);
  } else {
      valueA = valueA ?? '';
      valueB = valueB ?? '';
  }

  // Los valores vacíos quedan al final
  if (valueA === null && valueB === null) {
      return 0;
  }

  if (valueA === null) {
      return 1;
  }

  if (valueB === null) {
      return -1;
  }

  if (valueB < valueA) {
      return -1;
  }

  if (valueB > valueA) {
      return 1;
  }

  return 0;
}

function getComparator(order, orderBy, numeric = false) {
  return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy, numeric)
      : (a, b) => -descendingComparator(a, b, orderBy, numeric);
}
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
          return order;
      }
      return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const TableCellHead = styled(TableCell)(({ theme }) => ({
  color: "#3c4b64",
  fontWeight: "bold",
}));

const CustomTableRow = ({ headers, row, expandable, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow
        key={index}
        sx={
          index % 2 === 0
            ? { backgroundColor: "#f2f2f2" }
            : { backgroundColor: "#fff" }
        }
      >
        {expandable && (
          <TableCell>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
        )}
        {headers.map((header, index) => {
          return (
            <TableCell key={index} align={header.align ? header.align : "left"}>
              {row[header.id]}
            </TableCell>
          );
        })}
      </TableRow>
      {expandable && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={headers.length + 1}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>{row.expanded}</Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SimpleTable = ({
  headers,
  rows,
  loading = false,
  dense = false,
  expandable = false,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const selectedHeader = headers.find((header) => header.id === orderBy);
  const isNumericOrder = selectedHeader?.numeric === true;
  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => setPage(0), [rows]);

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
            {expandable && (
            <TableCell 
              sx={{
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                padding: 0,
              }}
            />
            )}
              {headers.map((header, index) => {
                return (
                  <TableCellHead
                    key={header.id}
                    align={header.align ? header.align : "left"}
                    width={header.align ? header.width : ""}
                    sortDirection={orderBy === header.id ? order : false}
                  >
                    {header.sortable && (
                      <TableSortLabel
                        active
                        direction={orderBy === header.id ? order : "asc"}
                        onClick={createSortHandler(header.id)}
                      />
                    )}
                    {header.label}
                  </TableCellHead>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading ? (
              <>
               {stableSort(rows, getComparator(order, orderBy, isNumericOrder))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <CustomTableRow
                        headers={headers}
                        row={row}
                        expandable={expandable}
                        index={index}
                        key={index}
                      />
                    );
                  })}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={!expandable ? headers.length : headers.length + 1}
                  align="center"
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}

            {/* {emptyRows > 0 && (
                            <TableRow
                                style={{
                                    height: 53 * emptyRows,
                                }}
                            >
                                <TableCell colSpan={!expandable ? headers.length : headers.length + 1} />
                            </TableRow>
                        )} */}

            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell
                  colSpan={!expandable ? headers.length : headers.length + 1}
                  align="center"
                >
                  <Typography variant="aclaracion">No hay datos</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default SimpleTable;
